import type { IntelOrgMemberCandidateDto } from '@@types/api.types';
import { FormLabel } from '@components/shared/form-label.component';
import { Button } from '@components/ui/button';
import { Command, CommandInput, CommandItem, CommandList } from '@components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { useDebounce } from '@hooks/use-debounce.hook';
import { cn } from '@lib/utils';
import { useAdminIntelMemberCandidates } from '@queries/intel-organization.query';
import { Check, ChevronsUpDown, Loader2Icon } from 'lucide-react';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

const SEARCH_DEBOUNCE_MS = 250;

type AdminIntelMemberPickerProps = {
  companyId: string;
  disabled?: boolean;
  hasError?: boolean;
  isRequired?: boolean;
  label?: string;
  onChange: (userId: string) => void;
  value: string | null | undefined;
};

// AZ-451 D9: server-searched, paginated user picker for adding Intelligence-org members. Mirrors the
// BW facilitator-dropdown pattern — debounced server search + infinite scroll (IntersectionObserver
// sentinel). Renders name + email (so same-named users are distinguishable) and disables candidates
// already in another Intelligence org (`eligible: false`).
export const AdminIntelMemberPicker: FC<AdminIntelMemberPickerProps> = (props) => {
  const { companyId, disabled = false, hasError, isRequired = false, label, onChange, value } = props;

  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState<IntelOrgMemberCandidateDto | null>(null);

  useDebounce(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS, [searchInput]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } = useAdminIntelMemberCandidates({
    companyId,
    search: debouncedSearch,
  });

  const candidates = useMemo(() => {
    const byId = new Map<string, IntelOrgMemberCandidateDto>();
    for (const page of data?.pages ?? []) {
      for (const item of page.items) {
        byId.set(item.id, item);
      }
    }
    return [...byId.values()];
  }, [data]);

  // Keep the trigger label resolvable even after the selected user scrolls out of the loaded pages.
  useEffect(() => {
    if (!value) {
      setSelectedCandidate(null);
      return;
    }
    const match = candidates.find((candidate) => candidate.id === value);
    if (match) {
      setSelectedCandidate(match);
    }
  }, [value, candidates]);

  const handleSelect = (candidate: IntelOrgMemberCandidateDto): void => {
    if (!candidate.eligible) {
      return;
    }
    setSelectedCandidate(candidate);
    onChange(candidate.id);
    setOpen(false);
  };

  const observerRef = useRef<IntersectionObserver | null>(null);
  const attachSentinel = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node) {
        return;
      }

      const root = node.closest('[cmdk-list]');
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting) && hasNextPage && !isFetchingNextPage) {
            void fetchNextPage();
          }
        },
        { root: root instanceof HTMLElement ? root : null, rootMargin: '120px 0px' },
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  const triggerLabel = selectedCandidate?.fullName || selectedCandidate?.email || 'Select an intelligence user';

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <FormLabel
          isRequired={isRequired}
          label={label}
        />
      )}
      <Popover
        onOpenChange={setOpen}
        open={open}
      >
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            aria-invalid={hasError}
            className={cn(
              'h-10 w-full min-w-0 justify-between rounded-md border bg-gray-100 px-3 py-2 font-normal text-base md:text-sm',
              selectedCandidate ? 'text-gray-600 hover:text-gray-600' : 'text-gray-400 hover:text-gray-400',
              'border-gray-200 focus-visible:border-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300',
              'aria-invalid:border-red-500 aria-invalid:focus-visible:ring-red-200',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
            disabled={disabled}
            role="combobox"
            variant="outline"
          >
            <span className="truncate">{triggerLabel}</span>
            <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="p-0"
          style={{ width: 'var(--radix-popover-trigger-width)' }}
        >
          <Command shouldFilter={false}>
            <CommandInput
              onValueChange={setSearchInput}
              placeholder="Search by name or email"
              value={searchInput}
            />
            <CommandList>
              {isPending ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2Icon className="size-4 animate-spin text-gray-400" />
                </div>
              ) : candidates.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                  No approved intelligence users available.
                </div>
              ) : (
                <div className="p-1">
                  {candidates.map((candidate) => {
                    const name = candidate.fullName || candidate.email;
                    return (
                      <CommandItem
                        disabled={!candidate.eligible}
                        key={candidate.id}
                        onSelect={() => handleSelect(candidate)}
                        value={candidate.id}
                      >
                        <Check
                          className={cn('mr-2 size-4 shrink-0', value === candidate.id ? 'opacity-100' : 'opacity-0')}
                        />
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate font-medium text-gray-900">{name}</span>
                          <span className="truncate text-xs text-gray-500">{candidate.email}</span>
                        </div>
                        {!candidate.eligible && (
                          <span className="ml-2 shrink-0 text-xs text-gray-400">In another organization</span>
                        )}
                      </CommandItem>
                    );
                  })}
                  {hasNextPage && (
                    <div
                      aria-hidden="true"
                      className="h-px w-full"
                      ref={attachSentinel}
                    />
                  )}
                  {isFetchingNextPage && (
                    <div className="flex items-center justify-center py-2">
                      <Loader2Icon className="size-4 animate-spin text-gray-400" />
                    </div>
                  )}
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
